import React, { useState, useContext, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom"; // Import Link from react-router-dom
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // Fix import
import MFAConfirmModal from "./MFAConfirmModal";
import { login, fetchProfileWithToken } from "../services/authAPI";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMFACode] = useState("");
  const [isMFAVisible, setIsMFAVisible] = useState(false);
  const [error, setError] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [isAccountSelectionOpen, setIsAccountSelectionOpen] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [authToken, setAuthToken] = useState("");
  const [authRefreshToken, setAuthRefreshToken] = useState(""); // Store refresh token from login
  const [searchParams] = useSearchParams();
  const [successMessage, setSuccessMessage] = useState("");
  const [isEmailAutoFilled, setIsEmailAutoFilled] = useState(false);
  const [isPasswordAutoFilled, setIsPasswordAutoFilled] = useState(false);

  // Use searchParams after it's initialized
  const successParam = searchParams.get("success");

  const {
    setIsLoggedIn,
    updateTokens,
    setMember,
    setIsAdmin,
    setIsServiceProvider,
  } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleAccountSelection = (account) => {
    setIsAccountSelectionOpen(false);
    
    // Determine user roles based on selected account
    const isServiceProvider = account.account_type === "SERVICE_PROVIDER";
    const isAdmin = !isServiceProvider && 
      account.subscription?.role_code?.toUpperCase() === "ADMIN";
    
    // Update context with the selected account info
    updateTokens(authToken, authRefreshToken);
    setMember({...memberData, selected_account: account, acct_id: account.id});
    setIsLoggedIn(true);
    setIsAdmin(isAdmin);
    setIsServiceProvider(isServiceProvider);

    // Save to localStorage
    localStorage.setItem("token", authToken);
    localStorage.setItem("refreshToken", authRefreshToken);
    localStorage.setItem("member", JSON.stringify({...memberData, selected_account: account}));
    localStorage.setItem("isAdmin", JSON.stringify(isAdmin));
    localStorage.setItem("isServiceProvider", JSON.stringify(isServiceProvider));
    localStorage.setItem("isLoggedIn", JSON.stringify(true));

    // Redirect based on isServiceProvider
    if (isServiceProvider) {
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  const handleAutoFill = useCallback((event) => {
    if (event.animationName === "mui-auto-fill") {
      if (event.target.name === "email") {
        setEmail(event.target.value);
        setIsEmailAutoFilled(true);
      }

      if (event.target.name === "password") {
        setPassword(event.target.value);
        setIsPasswordAutoFilled(true);
      }
    }

    if (event.animationName === "mui-auto-fill-cancel") {
      if (event.target.name === "email") {
        setIsEmailAutoFilled(false);
      }

      if (event.target.name === "password") {
        setIsPasswordAutoFilled(false);
      }
    }
  }, []);

  useEffect(() => {
    // Set success message based on URL parameter
    if (successParam === "account-added") {
      setSuccessMessage("Account successfully added to your profile!");
    }
  }, [successParam]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setError("");
    try {
      // Call login API
      const data = await login(email, password, mfaCode);

      // Check if MFA is required
      if (data.hasOwnProperty("needs_mfa")) {
        setIsMFAVisible(true);
        return;
      }

      setMFACode("");

      // Check if we have new token structure or old structure
      const accessToken = data.access_token || data.token;
      const refreshToken = data.refresh_token || "";
      
      const decodedToken = jwtDecode(accessToken);
      setAuthToken(accessToken);
      setAuthRefreshToken(refreshToken); // Store refresh token in component state

      // Fetch additional member details
      const memberData = await fetchProfileWithToken(decodedToken.member_id, accessToken);
      setMemberData(memberData);

      // Check if user has multiple accounts
      if (memberData.accounts && memberData.accounts.length > 1) {
        setAccounts(memberData.accounts);
        setIsAccountSelectionOpen(true);
        return;
      }

      // If only one account, use it directly
      if (memberData.accounts && memberData.accounts.length === 1) {
        const account = memberData.accounts[0];
        const isServiceProvider = account.account_type === "SERVICE_PROVIDER";
        const isAdmin = !isServiceProvider && 
          account.subscription?.role_code?.toUpperCase() === "ADMIN";
        
        updateTokens(accessToken, refreshToken);
        setMember({...memberData, selected_account: account, acct_id: account.id});
        setIsLoggedIn(true);
        setIsAdmin(isAdmin);
        setIsServiceProvider(isServiceProvider);

        localStorage.setItem("token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("member", JSON.stringify({...memberData, selected_account: account}));
        localStorage.setItem("isAdmin", JSON.stringify(isAdmin));
        localStorage.setItem("isServiceProvider", JSON.stringify(isServiceProvider));
        localStorage.setItem("isLoggedIn", JSON.stringify(true));

        // Redirect based on isServiceProvider
        if (isServiceProvider) {
          navigate("/admin");
        } else {
          navigate("/");
        }
      } else {
        throw new Error("No accounts found for this user");
      }
    } catch (err) {
      setEmail("");
      setPassword("");
      setMFACode("");
      setAuthToken("");
      setMemberData(null);
      
      // Handle specific error messages
      if (err.response) {
        // API error response
        if (isMFAVisible) {
          setError("Login failed, MFA code is incorrect");
        } else {
          setError("Login failed, Username or password is incorrect");
        }
      } else if (err.message === "No accounts found for this user") {
        setError(err.message);
      } else if (err.message === "Failed to fetch profile data") {
        setError("No accounts to log in to");
      } else {
        setError(err.message || "An unexpected error occurred");
      }
    }
  };

  

  return (
    <Box mt={8}>
      <Container maxWidth="sm">
        <Typography variant="h4" component="h1" gutterBottom>
          Login
        </Typography>
        {/* Show success message if present */}
        {successMessage && (<Alert severity="success">{successMessage}</Alert>)}
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            slotProps={{
              input: {
                onAnimationStart: handleAutoFill,
                ...(isMFAVisible && { readOnly: true })
              },
              inputLabel: {
                shrink: isEmailAutoFilled || email.length > 0,
              },
              htmlInput: {
                name: "email",
                autoComplete: "username"
              }
            }}
            onChange={(e) => {
              setEmail(e.target.value);
              if (isEmailAutoFilled) {
                setIsEmailAutoFilled(false);
              }
            }}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            slotProps={{
              input: {
                onAnimationStart: handleAutoFill,
                ...(isMFAVisible && { readOnly: true })
              },
              inputLabel: {
                shrink: isPasswordAutoFilled || password.length > 0,
              },
              htmlInput: {
                name: "password",
                autoComplete: "current-password"
              }
            }}
            onChange={(e) => {
              setPassword(e.target.value);
              if (isPasswordAutoFilled) {
                setIsPasswordAutoFilled(false);
              }
            }}
          />

          <MFAConfirmModal
          open={isMFAVisible}
          onClose={() => {setIsMFAVisible(false);}}
          email={email}
          password={password}
          mfaCode={mfaCode}
          setMFACode={setMFACode}
          setError={setError}
          handleSubmit={handleSubmit}
          setOpen={setIsMFAVisible}
          />

          <Button type="submit" variant="contained" color="primary" fullWidth>
            Login
          </Button>
        </form>
        <Box mt={2} textAlign="center">
          <Typography variant="body2">
            <Link to="/forgot-password">Forgot Password?</Link>
          </Typography>
        </Box>
      </Container>

      {/* Account Selection Dialog */}
      <Dialog open={isAccountSelectionOpen} onClose={() => setIsAccountSelectionOpen(false)}>
        <DialogTitle>Choose Your Account</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are registered with more than one account.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
          Please select which account to use.
          </Typography>
          <List>
            {accounts.map((account) => (
              <ListItem 
                button 
                key={account.id} 
                onClick={() => handleAccountSelection(account)}
              >
                <ListItemText primary={account.display_name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAccountSelectionOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LoginForm;
